import { useContext, useEffect, useMemo } from 'react'
import { Divider, DurationSelectorDHMS } from '@/components'
import CampaignIntegrationBlock from './CampaignIntegrationBlock'
import { Campaign, CampaignEventType, CampaignTemplateRead, CustomAttribute, PageLayoutType } from '@/api/dashboard'
import { isCustomEvent, isManualLaunchCampaign } from '../Settings'
import CustomAttributeTable from './CustomAttributeTable'
import {
  ButtonIcon,
  CheckBoxContainer,
  FieldGroup,
  FieldSection,
  InputCopyButton,
  SelectMulti,
  SuperAdminInfo,
  ToastSeverity,
  useToast,
} from '@/ui'
import { CampaignContext, ICampaignContext } from '@/expr/Context'
import { ItemTimelimit, validateInterval } from '@/layouts/components/ItemTimelimit'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import {
  Button,
  Checkbox,
  FormErrorMessage,
  Input,
  ModalPresetConfirm,
  ModalTitle,
  cn,
  formatHookFormErrors,
  useModal,
} from '@dashboard/ui'
import { ChevronLeft, CloseOutline } from '@/icons'
import SettingsHeader from '@/layouts/campaigns/SettingsHeader'
import { useTranslation } from 'react-i18next'
import { uniqueList } from '@/util'
import { DangerousAction } from '@/components/ui/DangerousAction/DangerousAction'
import { DisableCampaignModal } from '@/layouts/campaigns/widgets/DisableCampaignModal'
import { dashboardClient, getErrorText } from '@/api'
import { useParams } from 'react-router-dom'
import { useGameSettingsQuery } from '@/api/useGameSettingsQuery'
import { usePagesQuery } from '@/layouts/pages/api/usePagesQuery'
import { ErrorMessage } from '@hookform/error-message'
import { useCurrentUser } from '@/api/useCurrentUser'
import { useQuery } from '@tanstack/react-query'
import { campaignTemplatesQuery } from '@/layouts/campaigns/api/campaignTemplatesQuery'
import i18next from 'i18next'

export const CampaignSettings = (props: {
  onClose: () => void
  backClick?: () => void
  campaign: Campaign
  setCampaign: (c: Campaign) => void
}) => {
  const { companyId, gameId, eventId } = useParams() as {
    companyId: string
    gameId: string
    eventId: string
  }

  const { data: pages = [] } = usePagesQuery(companyId, gameId, {
    limit: 100,
  })

  const { isSuperAdmin } = useCurrentUser()
  const { data: templates = [], refetch } = useQuery(campaignTemplatesQuery(companyId, gameId))

  const pagesForLiveops = useMemo(() => {
    return pages
      .filter(page => (!page.type || page.type == PageLayoutType.Blocks) && page.slug != 'store')
      .sort((a, b) => a.title.localeCompare(b.title))
  }, [pages])

  const { data: settings } = useGameSettingsQuery(companyId, gameId)
  const showToast = useToast()
  const context = useContext(CampaignContext) as ICampaignContext
  const { t } = useTranslation()
  const methods = useForm<Campaign>({
    values: {
      ...props.campaign,
    },
  })

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
    setValue,
    control,
    watch,
    setError,
    clearErrors,
  } = methods

  useEffect(() => {
    reset(props.campaign)
  }, [props.campaign])

  const [start_at, end_at, version] = watch(['start_at', 'end_at', 'version'])

  const onSaveClick = async (data: Campaign) => {
    data.time_limit = !!(data.start_at || data.end_at)

    const err = validateInterval(data.start_at, data.end_at, t)
    if (err) {
      setError('start_at', { message: err })
      return
    } else {
      clearErrors('start_at')
    }

    if (isCustomEvent(data.type) && data.attributes?.custom_attributes) {
      let attributes = data.attributes.custom_attributes
      if (uniqueList(attributes, 'name').length != attributes.length) {
        setError('attributes.custom_attributes', {
          message: t('campaign.custom-event.attributes.duplicate'),
        })
        return
      }
    }

    props.setCampaign(data)
    if (props.campaign.id) {
      await dashboardClient.v1.updateCampaign(companyId, gameId, eventId, {
        description: data.description,
        start_at: data.start_at,
        end_at: data.end_at,
        version_description: data.version_description,
        attributes: data.attributes,
        allowed_popups_on_store: data.allowed_popups_on_store,
        is_sync: data.is_sync,
      })
      showToast({ message: t('saved'), severity: ToastSeverity.success })
      props.onClose()
    }
  }

  const setAttributes = (v: CustomAttribute[]) => {
    clearErrors('attributes.custom_attributes')
    setValue('attributes.custom_attributes', v, { shouldDirty: true })
  }

  const saveAsTemplate = async () => {
    const { campaign, graph, viewPort } = context
    graph.view_port = viewPort
    try {
      await dashboardClient.v1.createOrUpdateCampaignTemplate(companyId, gameId, {
        graph: graph,
        type: campaign?.type as CampaignEventType,
        source_campaign_id: campaign.id,
        description: campaign.description as string,
      })
      showToast({ message: t('saved'), severity: ToastSeverity.success })
      refetch()
    } catch (e) {
      showToast({ message: getErrorText(e), severity: ToastSeverity.error })
    }
  }

  const openConfirmSaveAsTemplateModal = useModal(props => (
    <ModalPresetConfirm
      color="primary"
      title={{
        children: t('confirm.title'),
        subtitle: t('campaign.confirm.save-as-template'),
      }}
      submit={{
        callback: async () => {
          props.onClose()
          saveAsTemplate()
        },
        color: 'primary',
        label: t('yes'),
      }}
      cancel={{
        label: t('no'),
        callback: props.onClose,
      }}
      {...props}
    />
  ))

  const openConfirmDeleteTemplateModal = useModal<{ t: CampaignTemplateRead }>(props => (
    <ModalPresetConfirm
      {...props}
      title={{
        children: t('confirm.title'),
        subtitle: i18next.t('campaign.confirm.delete-template'),
      }}
      cancel={{
        label: t('no'),
      }}
      submit={{
        color: 'danger',
        callback: async () => {
          props.onClose()
          await dashboardClient.v1.deleteCampaignTemplate(companyId, gameId, props.t.id)
          showToast({ message: t('removed'), severity: ToastSeverity.success })
          refetch()
        },
        label: t('remove'),
      }}
    />
  ))

  const renderAttributes = () => {
    if (props.campaign.type == CampaignEventType.AbandonedCart) {
      return (
        <>
          <Divider />
          <FieldSection label={t('campaign.header.settings')}>
            <Controller
              control={control}
              name="attributes.abandoned_cart_duration"
              render={({ field }) => (
                <DurationSelectorDHMS
                  size={props.campaign.id ? 'small' : 'middle'}
                  duration={field.value == undefined ? 3600 : field.value}
                  label={t('campaign.editor.abandoned_cart_duration')}
                  caption={t('campaign.editor.abandoned_cart_duration.caption')}
                  onChange={v => {
                    field.onChange(v)
                  }}
                />
              )}
            />
          </FieldSection>
        </>
      )
    }

    switch (props.campaign.type) {
      case CampaignEventType.GameHubVisit:
      case CampaignEventType.GameHubVisitStore:
        break
      default:
        return <></>
    }

    return (
      <>
        <Divider />

        <FieldSection label={t('campaign.header.settings')}>
          {props.campaign.type === CampaignEventType.GameHubVisit && (
            <FieldGroup
              label={t('campaign.editor.attributes.page_slugs')}
              caption={t('campaign.editor.attributes.page_slugs.caption')}
            >
              <Controller
                control={control}
                name="attributes.page_slugs"
                render={({ field }) => (
                  <SelectMulti
                    valueMode="multiple"
                    items={pagesForLiveops
                      .map(item => ({
                        children: item.title,
                        value: item.slug,
                      }))
                      .sort((a, b) => a.children.localeCompare(b.children))}
                    value={field.value || []}
                    onChange={v => {
                      field.onChange(v)
                    }}
                  />
                )}
              />
              <ErrorMessage
                name="attributes.page_slugs"
                errors={errors}
                render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
              />
            </FieldGroup>
          )}

          <Controller
            control={control}
            name="attributes.visit_duration"
            render={({ field }) => (
              <DurationSelectorDHMS
                size={props.campaign.id ? 'small' : 'middle'}
                duration={field.value == undefined ? 3600 : field.value}
                label={t('campaign.editor.visit_duration')}
                caption={t('campaign.editor.visit_duration.caption')}
                onChange={v => {
                  field.onChange(v)
                }}
              />
            )}
          />
        </FieldSection>
      </>
    )
  }

  const renderTimeLimitations = () => {
    return (
      <ItemTimelimit
        setValue={(prop, value) => {
          clearErrors('start_at')
          setValue(prop as 'start_at' | 'end_at', value, { shouldDirty: true })
        }}
        start_at={start_at}
        end_at={end_at}
        error={errors.start_at?.message}
      />
    )
  }

  const openConfirmDeleteData = useModal(props2 => <DisableCampaignModal {...props2} campaignId={props.campaign.id} />)

  const renderSyncSettings = () => {
    return (
      <>
        <Divider />

        <FieldSection
          label={
            <div className="flex items-center gap-2">
              {t('campaign.is_sync.settings')} <SuperAdminInfo />
            </div>
          }
        >
          <Checkbox caption={t('campaign.is_sync.caption')} {...register('is_sync')}>
            <div className="flex items-center gap-2" data-testid={'new-campaign/is-sync-checkbox'}>
              {t('campaign.is_sync')}
            </div>
          </Checkbox>
        </FieldSection>
      </>
    )
  }

  const renderCleanData = () => {
    return (
      <>
        <Divider />

        <FieldSection label={t('campaign.clean-data.section')} caption={t('campaign.clean-data.desc')}>
          <DangerousAction
            onClick={openConfirmDeleteData}
            btnText={t('campaign.clean-data.button')}
            title={t('campaign.btn.desc')}
          />
        </FieldSection>
      </>
    )
  }

  const renderTemplateSettings = () => {
    const currentTemplate = templates.find(it => it.source_campaign_id == context.campaign?.id)
    return (
      <>
        <Divider />

        <FieldSection
          label={
            <div className="flex items-center gap-2">
              Template settings <SuperAdminInfo />
            </div>
          }
          caption={currentTemplate && t('campaign.template-source')}
        >
          <div className="flex flex-col items-start gap-2">
            <Button onClick={() => openConfirmSaveAsTemplateModal()} size="sm" variant="outline">
              {t('campaign.header.save-template')}
            </Button>
            {currentTemplate && (
              <Button
                onClick={() => openConfirmDeleteTemplateModal({ t: currentTemplate })}
                size="sm"
                variant="outline"
                color="danger"
              >
                {t('campaign.header.delete-template')}
              </Button>
            )}
          </div>
        </FieldSection>
      </>
    )
  }

  const renderCustomAttributes = () => {
    return (
      isCustomEvent(props.campaign.type) && (
        <>
          <Divider />

          <FieldSection label={t('campaign.custom-event.custom-action')}>
            <FieldGroup label={t('campaign.custom-event.name')}>
              <Input {...register('type')} {...formatHookFormErrors(errors, 'type')} />
            </FieldGroup>
            <FieldGroup label={t('campaign.custom-event.desc')}>
              <Input {...register('name')} {...formatHookFormErrors(errors, 'name')} />
            </FieldGroup>
          </FieldSection>

          <Divider />

          <FieldSection label={t('campaign.custom-event.attributes')}>
            <FormProvider {...methods}>
              <CustomAttributeTable
                attributes={watch('attributes.custom_attributes') || []}
                setAttributes={setAttributes}
              />
            </FormProvider>
          </FieldSection>
        </>
      )
    )
  }

  const renderCreateHeader = () => {
    return (
      <ModalTitle className="w-full">
        <div className="flex items-center justify-between">
          <div>
            <ButtonIcon variant="outline" color="secondary" size="xs" onClick={props.backClick} className="mr-6">
              <ChevronLeft size={16} />
            </ButtonIcon>
            {t('campaign.step3')}
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={handleSubmit(onSaveClick)}
              size="sm"
              color="primary"
              data-testid={'new-campaign/create-button'}
            >
              {t('campaign.create')}
            </Button>
            <ButtonIcon variant="outline" color="secondary" size="sm" onClick={props.onClose}>
              <CloseOutline size={16} />
            </ButtonIcon>
          </div>
        </div>
      </ModalTitle>
    )
  }

  return (
    <div
      style={{ pointerEvents: context?.readOnly ? 'none' : 'all', opacity: context?.readOnly ? 0.6 : 1 }}
      className="h-full"
    >
      {props.campaign.id ? (
        <SettingsHeader
          text={t('campaign.header.settings')}
          onSave={handleSubmit(onSaveClick)}
          onClose={props.onClose}
          className="mb-4"
        />
      ) : (
        renderCreateHeader()
      )}

      <div className={cn(props.campaign.id ? 'px-6' : 'h-full overflow-auto px-6')}>
        {!props.campaign?.archived_at && !props.campaign?.id && (
          <>
            <FieldSection label={t('campaign.editor.campaign-published')}>
              <CheckBoxContainer
                checked={watch('enabled')}
                onChange={v => {
                  setValue('enabled', v.target.checked, { shouldDirty: true })
                }}
                label={t('campaign.editor.campaign-published')}
                caption={t('campaign.editor.campaign-published.subtitle')}
              />
            </FieldSection>
            <Divider />
          </>
        )}

        <FieldSection label={t('campaign.general')}>
          <FieldGroup label={t('campaign.description.desc')}>
            <Input {...register('description')} {...formatHookFormErrors(errors, 'description')} />
          </FieldGroup>

          {props.campaign.id && settings?.campaign_settings?.liveops_advanced_mode_enabled && (
            <>
              <FieldGroup label={t('campaign.version.current')}>
                <InputCopyButton value={version} disabled />
              </FieldGroup>
              <FieldGroup label={t('campaign.version_description')}>
                <Input {...register('version_description')} {...formatHookFormErrors(errors, 'version_description')} />
              </FieldGroup>
            </>
          )}

          {props.campaign.id && (
            <Checkbox
              caption={t('campaign.block.BrowserPopupNotificationActionNode.desc')}
              {...register('allowed_popups_on_store')}
            >
              {t('campaign.block.BrowserPopupNotificationActionNode.allowed_popups_on_store')}
            </Checkbox>
          )}
        </FieldSection>

        <Divider />

        {renderTimeLimitations()}

        {isManualLaunchCampaign(props.campaign.type) && (
          <>
            <Divider />

            <FieldSection label={t('campaign.custom-event.integration')}>
              <CampaignIntegrationBlock campaign={props.campaign} />
            </FieldSection>
          </>
        )}

        {renderAttributes()}

        {renderCustomAttributes()}

        {props.campaign.id && renderCleanData()}

        {props.campaign.id &&
          isSuperAdmin &&
          props.campaign.type == CampaignEventType.GameHubLogin &&
          renderSyncSettings()}

        {props.campaign.id && isSuperAdmin && renderTemplateSettings()}

        <div className="h-[100px]" />
      </div>
    </div>
  )
}
